import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons'
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Flex,
  Form,
  FormInstance,
  Row
} from 'antd'
import { FormListFieldData } from 'antd/lib/form'
import { useMemo, memo, useState, useEffect, useRef } from 'react'

import { useInstitutionServiceGetInvestigationHierarchyEndpoint } from 'src/api/queries'
import { InvestigationTypeModule } from 'src/api/requests'
import { InternalInstitutionsSelect } from 'src/components'
import { normalizeInvestigationHierarchy } from 'src/features/persons/utils'
import { useUser } from 'src/hooks'
import { useLocales } from 'src/locales'

import { InvestigationHierarchyNode } from '../../types'

const SpecimenBlock = ({
  field,
  index,
  onRemove,
  form,
  investigationData,
  isScreening,
  onCategoryChange,
  preselectCategoryIds
}: {
  field: FormListFieldData
  index: number
  onRemove: () => void
  form?: FormInstance
  investigationData: InvestigationHierarchyNode[]
  isScreening?: boolean
  onCategoryChange?: () => void
  /** Categories ticked when the block opens (e.g. the recommended TB tests). */
  preselectCategoryIds?: string[]
}) => {
  const { t } = useLocales()

  const [currentInstitution, setCurrentInstitution] =
    useState<InvestigationHierarchyNode>()

  const institutionId = Form.useWatch(
    ['categories', index, 'institutionId'],
    form
  )

  // const resetCurrentInstitution = () => {
  //   form?.setFieldValue(['categories', index, 'currentInstitution'], [])
  // }

  // const onSelectInstitution = (value: string) => {
  //   const selected = investigationData.find((i) => i.value === value)
  //   setCurrentInstitution(selected)
  //   resetCurrentInstitution()
  // }

  useEffect(() => {
    if (!institutionId) {
      setCurrentInstitution(undefined)
      return
    }
    const selected = investigationData.find((i) => i.value === institutionId)
    setCurrentInstitution(selected)
  }, [institutionId, investigationData])

  // Tick the requested categories once, when the institution's list is known
  // and nothing was chosen yet.
  const hasPreselected = useRef(false)
  useEffect(() => {
    if (hasPreselected.current || !preselectCategoryIds?.length) return
    if (!currentInstitution?.children?.length) return

    hasPreselected.current = true
    const path = ['categories', index, 'currentInstitution']
    if (form?.getFieldValue(path)?.length) return

    const values = currentInstitution.children
      .filter((category) => {
        try {
          return preselectCategoryIds.includes(
            JSON.parse(String(category.value)).categoryId
          )
        } catch {
          return false
        }
      })
      .map((category) => category.value)

    if (values.length) form?.setFieldValue(path, values)
  }, [currentInstitution, preselectCategoryIds, index, form])

  return (
    <div
      style={{
        border: '1px solid #d9d9d9',
        borderRadius: 8,
        padding: 16,
        marginBottom: 16
      }}
    >
      {index > 0 && (
        <Flex
          align="center"
          justify="end"
          className="full-width"
          style={{ marginBottom: 16 }}
        >
          <Button
            type="text"
            danger
            icon={<MinusCircleOutlined />}
            onClick={onRemove}
          >
            {t('common.remove')}
          </Button>
        </Flex>
      )}

      <Row gutter={[16, 16]}>
        {/* Laboratory Selection */}
        {/* {!isScreening && (
          <Col span={24}>
            <Form.Item
              name={[field.name, 'institutionId']}
              label={t('laboratory_classifiers.select_institution')}
              rules={requiredRules}
            >
              <Select
                placeholder={t('laboratory_classifiers.select_institution')}
                options={investigationData}
                onChange={onSelectInstitution}
                allowClear
              />
            </Form.Item>
          </Col>
        )} */}

        {/* Investigation Categories for Current Institution */}
        <Col span={24}>
          <Form.Item
            name={[field.name, 'currentInstitution']}
            label={t('investigations.select_category')}
          >
            <Checkbox.Group
              onChange={(checkedValues) => {
                if (isScreening && checkedValues.length > 1) {
                  const lastValue = checkedValues.at(-1)
                  form?.setFieldValue(
                    ['categories', index, 'currentInstitution'],
                    [lastValue]
                  )
                  onCategoryChange?.()
                }
              }}
            >
              <Row gutter={[8, 8]}>
                {currentInstitution?.children?.map((category) => (
                  <Col key={category.value} span={8}>
                    <Checkbox value={category.value}>{category.label}</Checkbox>
                  </Col>
                ))}
              </Row>
            </Checkbox.Group>
          </Form.Item>
        </Col>
      </Row>
    </div>
  )
}

const InvestigationCategoriesComponent = ({
  isScreening,
  form,
  specimenTypeId,
  isTb,
  onCategoryChange,
  autoSelectCurrentLaboratory,
  preselectCategoryIds
}: {
  isScreening?: boolean
  form?: FormInstance
  specimenTypeId?: string
  isTb?: boolean
  onCategoryChange?: () => void
  autoSelectCurrentLaboratory?: boolean
  preselectCategoryIds?: string[]
}) => {
  const { t, currentLang } = useLocales()
  const { userData } = useUser()

  const categoriesForm = Form.useWatch('categories', form)

  const laboratoryInstitutionId = Form.useWatch('laboratoryInstitutionId', form)

  const currentInstitutionId = String(userData?.currentLegalEntityId ?? '')
  const [showLabSelect, setShowLabSelect] = useState(false)

  const { data: investigationHierarchy, isFetching: isHierarchyFetching } =
    useInstitutionServiceGetInvestigationHierarchyEndpoint(
      {
        institutionId: String(laboratoryInstitutionId),
        module: isScreening ? InvestigationTypeModule.SCREENING : undefined,
        ...(specimenTypeId && { specimenTypeIds: [String(specimenTypeId)] }),
        isTb
      },
      [
        String(userData?.currentLegalEntityId),
        String(specimenTypeId),
        String(laboratoryInstitutionId),
        String(isTb)
      ],
      { enabled: !!userData?.currentLegalEntityId && !!laboratoryInstitutionId }
    )

  const normalizedHierarchy = useMemo(() => {
    if (!investigationHierarchy) return []

    return normalizeInvestigationHierarchy({
      item: investigationHierarchy,
      lang: currentLang.value,
      t
    })
  }, [investigationHierarchy, currentLang.value, t])

  const defaultInstitution = normalizedHierarchy.at(-1)

  const selectedInvestigationIds = useMemo(
    () =>
      (categoriesForm || [])
        .map((cat: { institutionId: string }) => cat.institutionId)
        .filter(Boolean),
    [categoriesForm]
  )

  useEffect(() => {
    if (!defaultInstitution) return
    if (selectedInvestigationIds.includes(defaultInstitution.value)) return

    const categories = form?.getFieldValue('categories') || []
    const firstEmptyIndex = categories.findIndex(
      (cat: { institutionId: string }) => !cat.institutionId
    )

    if (firstEmptyIndex !== -1) {
      form?.setFieldValue(
        ['categories', firstEmptyIndex, 'institutionId'],
        defaultInstitution.value
      )
    }
  }, [defaultInstitution, selectedInvestigationIds, form])

  // Auto-select the current institution as the laboratory instead of asking
  // the user to pick one.
  useEffect(() => {
    if (
      autoSelectCurrentLaboratory &&
      currentInstitutionId &&
      !laboratoryInstitutionId
    ) {
      form?.setFieldValue('laboratoryInstitutionId', currentInstitutionId)
    }
  }, [
    autoSelectCurrentLaboratory,
    currentInstitutionId,
    laboratoryInstitutionId,
    form
  ])

  // If the current institution has no investigations configured, reveal the
  // selector so the test can be routed to another institution.
  useEffect(() => {
    if (
      autoSelectCurrentLaboratory &&
      laboratoryInstitutionId === currentInstitutionId &&
      !isHierarchyFetching &&
      normalizedHierarchy.length === 0
    ) {
      setShowLabSelect(true)
    }
  }, [
    autoSelectCurrentLaboratory,
    laboratoryInstitutionId,
    currentInstitutionId,
    isHierarchyFetching,
    normalizedHierarchy.length
  ])

  return (
    <>
      <Form.Item
        name="laboratoryInstitutionId"
        label={t('laboratory_classifiers.select_laboratory_institution')}
        hidden={autoSelectCurrentLaboratory && !showLabSelect}
      >
        <InternalInstitutionsSelect
          placeholder={t(
            'laboratory_classifiers.select_laboratory_institution'
          )}
        />
      </Form.Item>

      {autoSelectCurrentLaboratory &&
        showLabSelect &&
        !isHierarchyFetching &&
        normalizedHierarchy.length === 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t(
              'laboratory_classifiers.no_investigations_for_current_institution'
            )}
          />
        )}

      {laboratoryInstitutionId && (
        <Form.List name="categories" initialValue={[{}]}>
          {(fields, { add, remove }) => (
            <div>
              {fields.map((field, index) => {
                const currentValue = form?.getFieldValue([
                  'categories',
                  index,
                  'institutionId'
                ])

                const investigationData = normalizedHierarchy.filter(
                  (item) =>
                    !selectedInvestigationIds.includes(item.value) ||
                    item.value === currentValue
                )

                return (
                  <SpecimenBlock
                    key={field.key}
                    field={field}
                    index={index}
                    onRemove={() => remove(index)}
                    form={form}
                    investigationData={investigationData}
                    isScreening={isScreening}
                    onCategoryChange={onCategoryChange}
                    preselectCategoryIds={
                      index === 0 ? preselectCategoryIds : undefined
                    }
                  />
                )
              })}

              {!isScreening && (
                <Button
                  type="dashed"
                  onClick={() => add({})}
                  block
                  icon={<PlusOutlined />}
                >
                  {t('consultations.add_institution')}
                </Button>
              )}
            </div>
          )}
        </Form.List>
      )}
    </>
  )
}

export const InvestigationCategories = memo(InvestigationCategoriesComponent)
